import { SettingsSection } from '@components'

import { Button, Checkbox, ComponentsGroup, ModalConfirmation, notify, Spacing, useModal } from '@billy/design-system'
import { parse } from 'qs'
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { CustomEvent } from '../../../../../../enums/customEvent'
import { useCustomEventListener } from '../../../../../../hooks/useCustomEventListener'
import { APIError } from '../../../../../../utils'
import { useDeleteVatRate } from '../../hooks/useDeleteVatRate'
import { useInvalidateVatRates } from '../../hooks/useInvalidateVatRates'
import { useTemplatesToCreateVatRates } from '../../hooks/useTemplatesToCreateVatRates'
import { CreateOrEditVatRateFromTemplateModal } from '../VatRateModal/CreateOrEditVatRateFromTemplateModal'
import { CreateOrEditVatRateModal } from '../VatRateModal/CreateOrEditVatRateModal'
import { getCreateModalId } from '../VatRateModal/shared/utils/getCreateModalId'
import { SalesVatRatesTable } from './elements/SalesVatRatesTable'
import * as Styled from './styles'

const DELETE_SALES_TAX_RATE_CONFIRMATION_MODAL_ID = 'delete-sales-tax-rate-confirmation'

export const SalesVatRates = (): ReactElement => {
  const { t } = useTranslation()
  const [isArchivedVisible, setIsArchivedVisible] = useState(false)
  const vatRateIdToDelete = useRef<string | undefined>()
  const { invalidateTaxRates } = useInvalidateVatRates()
  const { search } = useLocation()

  const createVatRateModalId = getCreateModalId('sales')

  const { open: openCreateVatRateModal } = useModal(createVatRateModalId)

  const { open: openDeleteConfirmation, close: closeDeleteConfirmation } = useModal(
    DELETE_SALES_TAX_RATE_CONFIRMATION_MODAL_ID,
    {
      onClose: () => {
        vatRateIdToDelete.current = undefined
      },
    },
  )

  const queryParams = parse(search || '', { ignoreQueryPrefix: true })
  const forceRenderCreateVatRateModal = useMemo(() => {
    return Boolean(queryParams?.openCreateVatRateModal)
  }, [queryParams?.openCreateVatRateModal])

  const onDeleteTaxRateError = useCallback(
    (error: APIError | undefined) => {
      notify({
        id: 'tax-ruleset-deleted',
        message: error?.message || t('settings.organization.vat_rates.rates.notification.delete_error'),
        variant: 'error',
      })
    },
    [t],
  )

  const onDeleteTaxRateSuccess = useCallback(() => {
    notify({
      id: 'tax-ruleset-deleted',
      message: t('settings.organization.vat_rates.rates.notification.delete_success'),
      variant: 'success',
    })
    closeDeleteConfirmation()
    invalidateTaxRates()
  }, [closeDeleteConfirmation, invalidateTaxRates, t])

  const { delete: deleteRuleset, isLoading: isDeleting } = useDeleteVatRate({
    onSuccess: onDeleteTaxRateSuccess,
    onError: onDeleteTaxRateError,
  })

  const onRateDelete = useCallback(
    (taxRateId: string) => {
      vatRateIdToDelete.current = taxRateId
      openDeleteConfirmation()
    },
    [openDeleteConfirmation],
  )

  const handleDeleteModalOkClick = useCallback(() => {
    if (vatRateIdToDelete.current) {
      deleteRuleset(vatRateIdToDelete.current)
    }
  }, [deleteRuleset])

  const handleOpenCreateVatRateModalRequested = useCallback(() => {
    openCreateVatRateModal()
  }, [openCreateVatRateModal])

  const handleCreateVatRateButtonClick = useCallback(() => {
    openCreateVatRateModal()
  }, [openCreateVatRateModal])

  const handleShowArchivedCheckboxChange = useCallback(() => {
    setIsArchivedVisible((currentValue) => !currentValue)
  }, [])

  const { isCreateNewVatRateFromTemplate: isNewVatRateFromTemplate } = useTemplatesToCreateVatRates()

  useCustomEventListener(CustomEvent.OpenCreateVatRateModalRequested, handleOpenCreateVatRateModalRequested)

  return (
    <>
      <SettingsSection>
        <SettingsSection.Content
          title={t('settings.organization.vat_rates.sales_vat_rates')}
          sideActions={
            <ComponentsGroup spacing={Spacing.S16} direction="column" centered={false}>
              <Button icon="circlePlus" variant="outlined" onClick={handleCreateVatRateButtonClick}>
                {t('settings.organization.vat_rates.purchase_vat_rates.create')}
              </Button>
              <Styled.CheckboxWrapper>
                <Checkbox onChange={handleShowArchivedCheckboxChange}>
                  {t('settings.organization.vat_rates.show_archived')}
                </Checkbox>
              </Styled.CheckboxWrapper>
            </ComponentsGroup>
          }
          subtitle={t('settings.organization.vat_rates.sales_vat_rates.description')}
        >
          <Styled.TableWrapper>
            <SalesVatRatesTable onRateDelete={onRateDelete} showArchived={isArchivedVisible} />
          </Styled.TableWrapper>
          <ModalConfirmation
            cancelLabel={t('cancel')}
            danger
            id={DELETE_SALES_TAX_RATE_CONFIRMATION_MODAL_ID}
            message={t('settings.organization.vat_rates.modals.delete.message')}
            okLabel={t('settings.organization.vat_rates.modals.delete.delete')}
            okLoading={isDeleting}
            onOk={handleDeleteModalOkClick}
            title={t('settings.organization.vat_rates.modals.delete.title')}
          />
        </SettingsSection.Content>
      </SettingsSection>
      {isNewVatRateFromTemplate ? (
        <CreateOrEditVatRateFromTemplateModal id={createVatRateModalId} forceRender={forceRenderCreateVatRateModal} />
      ) : (
        <CreateOrEditVatRateModal id={createVatRateModalId} />
      )}
    </>
  )
}
